import React, { useImperativeHandle, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useSettingsStore } from '@core/store';
import { useTranslation } from 'react-i18next';
import { WcnButton } from '@/components';

export interface UnsavedChangesDialogRef {
    openDialog: () => void;
    /** Close the dialog without triggering any callback — used by the parent after async operations. */
    closeDialog: () => void;
}

interface UnsavedChangesDialogProps {
    heading?: string;
    message?: string;
    /** Called when the user chooses to leave without saving. Dialog closes automatically. */
    onDiscard?: () => void;
    /** Called when the user chooses to stay. Dialog closes automatically. */
    onStay?: () => void;
    /**
     * Called when the user chooses to save and then leave.
     * The dialog does NOT close automatically — the caller must close it via the ref
     * after the async save completes (or fails).
     */
    onSaveAndLeave?: () => void;
    dialogRef?: React.Ref<UnsavedChangesDialogRef>;
}

const UnsavedChangesDialog = ({
    heading,
    message,
    onDiscard,
    onStay,
    onSaveAndLeave,
    dialogRef
}: UnsavedChangesDialogProps) => {
    const [open, setOpen] = useState(false);
    const { darkMode, isMobile } = useSettingsStore();
    const { t } = useTranslation();

    useImperativeHandle(dialogRef, () => ({
        openDialog: () => setOpen(true),
        closeDialog: () => setOpen(false)
    }));

    const handleDiscard = () => {
        onDiscard?.();
        setOpen(false);
    };

    const handleStay = () => {
        onStay?.();
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleStay}
            maxWidth="sm"
            fullWidth={isMobile}
            fullScreen={false}
            transitionDuration={{ enter: 225, exit: 0 }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minWidth: isMobile ? 'auto' : '800px',
                    maxWidth: '900px',
                    margin: isMobile ? 1 : 3,
                    backgroundColor: darkMode ? '#444444' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#000000'
                }
            }}
            BackdropProps={{
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
        >
            <Box sx={{ p: isMobile ? 1 : 3 }}>
                <DialogTitle
                    sx={{
                        fontSize: '1.25rem',
                        fontWeight: 400,
                        color: darkMode ? '#ffffff' : '#000000',
                        pb: 5,
                        pt: isMobile ? 5 : 0,
                        textAlign: 'center',
                        textTransform: 'uppercase'
                    }}
                >
                    {heading ?? t('dontForgetToSave')}
                </DialogTitle>

                <Box className="warning-icon" sx={{ display: 'flex', justifyContent: 'center', pb: 5 }}>
                    <i className="bi bi-exclamation-triangle" style={{ fontSize: 28, color: 'var(--color-warning)' }} />
                </Box>

                <DialogContent sx={{ pt: '8px !important', pb: 5 }}>
                    <DialogContentText
                        sx={{
                            fontSize: '1rem',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-line',
                            textAlign: 'center'
                        }}
                    >
                        {message ?? t('unsavedChangesLeave')}
                    </DialogContentText>
                </DialogContent>

                {isMobile ? (
                    <DialogActions
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            pb: 5
                        }}
                    >
                        {onSaveAndLeave && (
                            <WcnButton onClick={onSaveAndLeave} variant="primary" style={{ width: 240 }}>
                                {t('saveAndLeave')}
                            </WcnButton>
                        )}
                        <WcnButton onClick={handleStay} variant="primary" style={{ width: 240, margin: 0 }}>
                            {t('stayHere')}
                        </WcnButton>

                        <WcnButton onClick={handleDiscard} variant="red" style={{ width: 240, margin: 0 }}>
                            {t('discardChanges')}
                        </WcnButton>
                    </DialogActions>
                ) : (
                    <DialogActions
                        sx={{
                            padding: 3,
                            gap: 1,
                            justifyContent: 'center'
                        }}
                    >
                        <WcnButton onClick={handleDiscard} variant="red" style={{ width: 240 }}>
                            {t('discardChanges')}
                        </WcnButton>
                        <WcnButton onClick={handleStay} variant="primary" style={{ width: 240 }}>
                            {t('stayHere')}
                        </WcnButton>
                        {onSaveAndLeave && (
                            <WcnButton onClick={onSaveAndLeave} variant="primary" style={{ width: 240 }}>
                                {t('saveAndLeave')}
                            </WcnButton>
                        )}
                    </DialogActions>
                )}
            </Box>
        </Dialog>
    );
};

UnsavedChangesDialog.displayName = 'UnsavedChangesDialog';

export default UnsavedChangesDialog;
